import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { useAuthModal } from '@/features/auth/authModalStore';
import { authCallbackURL } from '@/features/auth/api/redirect';
import type { TabProps } from '../../settings';
import { Field, SettingsCard, SettingsRow, TRANSLUCENT_FIELD } from '../SettingsPrimitives';

export function AccountTab({ draft, set }: TabProps) {
  const { user, profile, signOut } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);
  const navigate = useNavigate();
  const signedIn = Boolean(user);

  async function onResetPassword() {
    const email = profile?.email || draft.email;
    if (!email) return toast('Set your email first', 'error');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authCallbackURL(),
    });
    toast(error ? error.message : `Reset link sent to ${email}`, error ? 'error' : 'ok');
  }

  async function onSignOutEverywhere() {
    if (!window.confirm('Sign out of every device, including this one?')) return;
    await signOut();
    navigate(ROUTES.home);
  }

  function onDeleteAccount() {
    if (window.prompt('Type DELETE to permanently remove your account.') !== 'DELETE') return;
    toast('Deletion request received. Check your email to confirm.', 'info');
  }

  return (
    <>
      <SettingsCard title="Account" desc="Account Info.">
        <Field
          label="Email"
          hint={
            signedIn
              ? 'Contact support to change the email tied to a verified account.'
              : 'Sign in to lock this email to your account.'
          }
        >
          <Input
            type="email"
            value={draft.email}
            disabled={signedIn}
            placeholder={signedIn ? '' : 'you@domain.com'}
            onChange={(e) => set('email', e.target.value)}
            className={`${TRANSLUCENT_FIELD} disabled:cursor-not-allowed disabled:opacity-50`}
          />
        </Field>

        {signedIn ? (
          <SettingsRow
            title="Password"
            desc="We'll email you a secure reset link."
            control={
              <Button type="button" variant="subtle" size="sm" onClick={onResetPassword}>
                Send reset link
              </Button>
            }
          />
        ) : (
          <SettingsRow
            title="You're not signed in"
            desc="Log in to manage your password, sessions, and 2FA."
            control={
              <Button type="button" variant="subtle" size="sm" onClick={openAuth}>
                Log in
              </Button>
            }
          />
        )}
      </SettingsCard>

      {signedIn && (
        <SettingsCard className="px-6 py-2">
          <SettingsRow
            title="Sign out everywhere"
            desc="Ends every active session, including this one."
            control={
              <Button type="button" variant="subtle" size="sm" onClick={onSignOutEverywhere}>
                Sign out
              </Button>
            }
          />
          <SettingsRow
            title="Delete account"
            desc="Permanently removes your account and content. Cannot be undone."
            control={
              <Button
                type="button"
                size="sm"
                className="bg-danger/10 text-danger hover:bg-danger/20"
                onClick={onDeleteAccount}
              >
                Delete
              </Button>
            }
          />
        </SettingsCard>
      )}
    </>
  );
}
